import {AfterViewInit, Component, ElementRef, Input, OnChanges, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {MatPaginator} from '@angular/material/paginator';
import {MatSort} from '@angular/material/sort';
import {MatTableDataSource} from '@angular/material/table';
import {Router} from '@angular/router';
import {CallLogEntryModel} from '@app/models/call-log-entry.model';
import {ContactModel} from '@app/models/contact.model';
import {GtAttendantService} from '@app/services/gt-attendant.service';
import {GtAuthenticationService} from '@app/services/gt-authentication.service';
import {GtDirectoryService} from '@app/services/gt-directory.service';
import {GtNotificationService} from '@app/services/gt-notification.service';
import {Click2callComponent} from '@app/shared/click2call/click2call.component';
import {ContactDetailComponent} from '@app/shared/contact-detail/contact-detail.component';
import {PhoneNumberFormatter} from '@app/shared/formatting.pipes';
import {BuilderFactory} from '@app/shared/utils/builder.factory';
import {Contact, Response} from '@gt-api';
import {TranslateService} from '@ngx-translate/core';
import {fromEvent, Observable, Subject} from 'rxjs';
import {debounceTime, filter, switchMap, takeUntil, tap} from 'rxjs/operators';

@Component({
  selector: 'gt-main-number-table',
  templateUrl: './main-number-table.component.html',
  styleUrls: ['./main-number-table.component.scss']
})
export class MainNumberTableComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
  @Input() logEntries: CallLogEntryModel[];
  @Input() isServiceNumberLogs: boolean;
  filterStr = '';
  mainNumberDataSource = new MatTableDataSource<CallLogEntryModel>();
  mainNumberDataSourceColumns: string[] =  [];
  @ViewChild('sortForDataSource') sortForDataSource: MatSort;
  @ViewChild('paginatorForDataSource') paginatorForDataSource: MatPaginator;
  @ViewChild('filter') filter: ElementRef;
  private destroyed = new Subject<void>();

  constructor(
    private authenticationService: GtAuthenticationService,
    private directoryService: GtDirectoryService,
    private dialog: MatDialog,
    private router: Router,
    private translateService: TranslateService,
    private attendantService: GtAttendantService,
    private notificationService: GtNotificationService
  ) {}

  ngOnInit() {
    this.mainNumberDataSource.filterPredicate = (entry: CallLogEntryModel, filter: string) => {
      const key = filter.trim().toLowerCase();
      const keyNoSpace = key.replace(/ /g, '');
      let matchesCaller = false;
      if (entry.caller) {
        matchesCaller = entry.caller.toLowerCase().replace(/ /g, '').indexOf(keyNoSpace) > -1;
      }
      return (
        matchesCaller ||
        entry.answeredBy.toLowerCase().replace(/ /g, '').indexOf(keyNoSpace) > -1 ||
        entry.fullNumber.indexOf(keyNoSpace) > -1 ||
        entry.accessNumber.toLowerCase().indexOf(key) > -1
      );
    };
  }

  ngAfterViewInit() {
    fromEvent(this.filter.nativeElement, 'keyup')
      .pipe(debounceTime(150), takeUntil(this.destroyed))
      .subscribe(() => {
        this.paginatorForDataSource.pageIndex = 0;
        this.mainNumberDataSource.filter = this.filter.nativeElement.value;
      });

    this.mainNumberDataSource.paginator = this.paginatorForDataSource;
    this.mainNumberDataSource.sort = this.sortForDataSource;
  }

  ngOnChanges(): void {
    this.mainNumberDataSourceColumns = ['time', 'status', 'caller', 'duration', 'accessNumber', 'answeredBy'];
    if (this.isServiceNumberLogs) {
      this.mainNumberDataSourceColumns.push('handledBy');
    }
    this.mainNumberDataSource.data = this.logEntries ? this.logEntries : [];
  }

  ngOnDestroy() {
    this.destroyed.next();
    this.destroyed.complete();
  }

  displayCallerName(entry: CallLogEntryModel): string {
    if (entry.caller === 'anonymous') {
      return this.translateService.instant('global.anonymous');
    }
    return entry.caller;
  }

  showContact(entry: CallLogEntryModel) {
    if (entry.isAnonymous()) {
      this.notificationService.errorNotification.next(this.translateService.instant('main_number_table.anon_caller'));
      return;
    }
    this.directoryService.getContactByFullNumber(this.authenticationService.oname, entry.fullNumber).subscribe(
      contact => {
        if (contact) {
          this.openContactDialog(ContactModel.convertContactModel(contact.backendModel));
        } else {
          this.openContactDialog(ContactModel.convertCallLogEntryModel(entry));
        }
      },
      _ => {
        this.openContactDialog(ContactModel.convertCallLogEntryModel(entry));
      }
    );
  }

  private openContactDialog(contact: ContactModel) {
    const dialogRef = this.dialog.open(ContactDetailComponent, {minWidth: 500});
    if (!contact.id) {
      const newContact = BuilderFactory.createBuilder<Contact>()
        .id(-1)
        .ownerLogin(this.authenticationService.login)
        .ownerOrganization(this.authenticationService.oname)
        .firstName(contact.name)
        .fullNumber(contact.phoneNumber)
        .build();
      newContact['@type'] = 'Contact';
      contact = ContactModel.convertContactModel(newContact);
    }
    dialogRef.componentInstance.contact = contact;
    dialogRef.componentInstance.back.subscribe(() => {
      dialogRef.close();
    });
  }

  sendSMS(entry: CallLogEntryModel, shareContact = false) {
    if (entry.isAnonymous()) {
      this.notificationService.errorNotification.next(this.translateService.instant('main_number_table.anon_caller'));
      return;
    }
    if (this.isServiceNumberLogs) {
      this.setCallLogHandled(entry).subscribe();
    }
    const contact = ContactModel.convertCallLogEntryModel(entry);
    if (shareContact) {
      const message = contact.name + '\n' + new PhoneNumberFormatter().transform(contact.phoneNumber);
      this.router.navigate(['/sms'], { queryParams: { msg: message } });
    } else {
      this.router.navigate(['/sms'], { queryParams: { receivers: contact.phoneNumber } });
    }
  }

  click2call(entry: CallLogEntryModel) {
    if (entry.isAnonymous()) {
      this.notificationService.errorNotification.next(this.translateService.instant('main_number_table.anon_caller'));
      return;
    }
    const dialogRef = this.dialog.open(Click2callComponent, {minWidth: 500});
    dialogRef.componentInstance.canEdit = false;
    dialogRef.componentInstance.initialPhoneNumber = entry.fullNumber;
    dialogRef.componentInstance.name = entry.caller;
    dialogRef.afterClosed().pipe(
      filter(result => !!result),
      switchMap(result => this.attendantService.click2Call(result.phoneNumber, result.displayNumber)),
      filter(response => response.code === 200),
      tap(response => this.notificationService.normalNotification.next(this.translateService.instant('main_number_table.call_init'))),
      filter(() => !!this.isServiceNumberLogs),
      switchMap(() => this.setCallLogHandled(entry)),
    ).subscribe();
  }

  setCallLogHandled(entry: CallLogEntryModel): Observable<Response> {
    return this.attendantService.setCallLogHandled(this.authenticationService.oname, this.authenticationService.login, entry.id).pipe(
      tap(() => {
        entry.handledBy = this.authenticationService.login;
      })
    );
  }

  clearFilter() {
    this.filterStr = '';
    this.mainNumberDataSource.filter = '';
  }
}
